'use client';

import clsx from 'clsx';
import { nanoid } from 'nanoid';
import { useState } from 'react';
import { StoryblokStory } from 'storyblok-generate-ts';
import { Swiper, SwiperClass, SwiperSlide } from 'swiper/react';

import { ExperienceCard } from '../ExperienceCard/ExperienceCard';

import { Typography } from '@/components/core';
import { useDragToScroll } from '@/hooks';
import { ExperienceStoryblok } from '@/types/storyblok/storyblok-types';

import styles from './ExperienceClient.module.scss';

type ExperienceClientProps = {
    items: StoryblokStory<ExperienceStoryblok>[];
    types: string[];
    hideTabs?: boolean;
};

const capitalizeFirstLetter = array =>
    array.map(item =>
        typeof item === 'string' && item.length > 0 ? item.charAt(0).toUpperCase() + item.slice(1).toLowerCase() : item,
    );
const filterItemsByType = (items, types) => types.map(type => items.filter(item => item.content.type.includes(type)));

export const ExperienceClient: React.FC<ExperienceClientProps> = ({ items, types, hideTabs }) => {
    const allTypes = ['All', ...capitalizeFirstLetter(types)];
    const [selectedIndex, setSelectedIndex] = useState(allTypes[0]);
    const filtered = [items, ...filterItemsByType(items, types)];

    const [swiper, setSwiper] = useState<SwiperClass>();

    const setTab = (type: string) => {
        setSelectedIndex(type);
        swiper.slideTo(allTypes.indexOf(type));
    };

    const { containerRef, onMouseDown, onMouseMove, onMouseLeaveOrUp } = useDragToScroll();

    return (
        <div className={clsx(styles.root, 'container')}>
            {!hideTabs && (
                <div
                    className={clsx(styles.menu)}
                    ref={containerRef}
                    onMouseDown={onMouseDown}
                    onMouseMove={onMouseMove}
                    onMouseLeave={onMouseLeaveOrUp}
                    onMouseUp={onMouseLeaveOrUp}
                >
                    {allTypes.map((type, index) => (
                        <div
                            key={type}
                            onClick={() => setTab(type)}
                            className={clsx(styles.menuItem, {
                                [styles.active]: selectedIndex === type,
                            })}
                        >
                            <Typography looksLike="pSmall">
                                {type} <label className={styles.length}>{filtered[index].length}</label>
                            </Typography>
                        </div>
                    ))}
                </div>
            )}

            <div className={clsx(styles.contentWrapper)}>
                <Swiper
                    autoHeight
                    onSwiper={setSwiper}
                    spaceBetween={50}
                    slidesPerView={1}
                    onSlideChangeTransitionEnd={obj => setSelectedIndex(allTypes[obj.activeIndex])}
                >
                    {filtered.map(group => (
                        <SwiperSlide
                            className={styles.slide}
                            key={nanoid()}
                        >
                            <div className={styles.innerWrap}>
                                {group.map(item => (
                                    <div
                                        className={styles.item}
                                        key={item.uuid}
                                    >
                                        <ExperienceCard
                                            blok={item.content}
                                            types={types}
                                        />
                                    </div>
                                ))}
                            </div>
                        </SwiperSlide>
                    ))}
                </Swiper>
            </div>
        </div>
    );
};
